import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, Package, Layers, Sparkles, ShieldCheck, Clock, Calendar } from 'lucide-react';
import type { LibraryAddon } from '@/hooks/useLibraryAddons';

type AddonVersion = LibraryAddon & { owned: boolean; hasPreviousVersion?: boolean; effectivePrice?: number };

interface Props {
  // F29.3: la card riceve l'intera famiglia con tutte le versioni
  versions: AddonVersion[];
  defaultVersion: AddonVersion;
  onPurchase: (addon: LibraryAddon) => void;
  isPurchasing: boolean;
  isBundle?: boolean;
  bundleSavings?: number;
  hasPendingContent?: boolean;
}

const categoryMeta: Record<string, { label: string; className: string }> = {
  brand: { label: 'Listino prezzi', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  stratigraphies: { label: 'Stratigrafie', className: 'bg-purple-50 text-purple-700 border-purple-200' },
  bundle: { label: 'Bundle completo', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  thematic: { label: 'Tematico', className: 'bg-amber-50 text-amber-700 border-amber-200' },
};

export default function LibraryCard({ versions, defaultVersion, onPurchase, isPurchasing, isBundle, bundleSavings, hasPendingContent }: Props) {
  const [selectedSlug, setSelectedSlug] = useState<string>(defaultVersion.slug);
  const addon: AddonVersion = useMemo(
    () => versions.find((v) => v.slug === selectedSlug) ?? defaultVersion,
    [versions, selectedSlug, defaultVersion]
  );

  // Fallback descrizioni: se la versione selezionata ha description vuote,
  // prendi da qualsiasi altra versione della famiglia che le ha.
  const fallbackShort = useMemo(
    () => addon.short_description ?? versions.find((v) => v.short_description)?.short_description ?? null,
    [addon, versions]
  );
  const fallbackLong = useMemo(
    () => addon.long_description ?? versions.find((v) => v.long_description)?.long_description ?? null,
    [addon, versions]
  );

  const meta = categoryMeta[addon.category] ?? categoryMeta.brand;
  const isComingSoon = addon.category === 'stratigraphies' && addon.preview_count_stratigraphies === 0;
  const showVersionPicker = versions.length > 1;

  // Nome senza anno (l'anno e' nel dropdown)
  const cleanName = addon.name.replace(/\s*\d{4}\s*$/, '');

  return (
    <Card className={`flex flex-col h-full transition-shadow hover:shadow-md ${isBundle ? 'border-emerald-300 ring-1 ring-emerald-200/60' : 'border-zinc-200'}`}>
      <CardHeader className="pb-3 relative">
        {isBundle && bundleSavings && bundleSavings > 0 && (
          <Badge className="absolute -top-2 right-3 bg-emerald-600 hover:bg-emerald-600 text-white shadow-sm">
            <Sparkles className="h-3 w-3 mr-1" />
            Risparmi €{bundleSavings.toFixed(0)}
          </Badge>
        )}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {addon.category === 'brand' && <Package className="h-5 w-5 text-blue-600 shrink-0" />}
            {addon.category === 'stratigraphies' && <Layers className="h-5 w-5 text-purple-600 shrink-0" />}
            {addon.category === 'bundle' && <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />}
            <h3 className="font-semibold text-zinc-900 truncate">{cleanName}</h3>
          </div>
          {addon.owned && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 shrink-0">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Acquistato
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          <Badge variant="outline" className={`${meta.className} text-[10px]`}>
            {meta.label}
          </Badge>
          {isComingSoon && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
              <Clock className="h-3 w-3 mr-1" />
              In arrivo
            </Badge>
          )}
          {addon.preview_count_materials > 0 && (
            <Badge variant="outline" className="bg-zinc-50 text-zinc-600 border-zinc-200 text-[10px]">
              <Package className="h-3 w-3 mr-1" />
              {addon.preview_count_materials.toLocaleString('it-IT')}+ materiali
            </Badge>
          )}
          {addon.preview_count_stratigraphies > 0 && (
            <Badge variant="outline" className="bg-zinc-50 text-zinc-600 border-zinc-200 text-[10px]">
              <Layers className="h-3 w-3 mr-1" />
              {addon.preview_count_stratigraphies} stratigrafie
            </Badge>
          )}
        </div>

        {/* F29.3: dropdown versione se ci sono multiple annate */}
        {showVersionPicker && (
          <div className="mt-3">
            <Select value={selectedSlug} onValueChange={setSelectedSlug}>
              <SelectTrigger className="h-8 text-xs">
                <Calendar className="h-3 w-3 mr-1 text-zinc-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {versions.map((v) => (
                  <SelectItem key={v.slug} value={v.slug}>
                    Versione {v.version_year ?? '?'}{v.is_current_year ? ' (corrente)' : ''}
                    {v.owned && ' ✓'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex-1 pb-4">
        {fallbackShort && <p className="text-sm text-zinc-600 mb-2">{fallbackShort}</p>}
        {fallbackLong && (
          <p className="text-xs text-zinc-500 whitespace-pre-line line-clamp-6">{fallbackLong}</p>
        )}
        {hasPendingContent && (
          <div className="mt-3 flex items-start gap-1.5 px-2 py-1.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[11px] leading-snug">
            <Clock className="h-3 w-3 mt-0.5 shrink-0" />
            <span>Una parte del contenuto (stratigrafie) è in fase di pubblicazione — verrà aggiunta automaticamente quando disponibile.</span>
          </div>
        )}
      </CardContent>
      <CardFooter className={`flex items-center justify-between pt-4 border-t ${isBundle ? 'bg-emerald-50/40' : 'bg-zinc-50/60'}`}>
        <div>
          {addon.hasPreviousVersion && addon.effectivePrice != null && addon.effectivePrice < Number(addon.price_eur) ? (
            <>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-emerald-700">€{addon.effectivePrice.toFixed(0)}</span>
                <span className="text-sm line-through text-zinc-400">€{Number(addon.price_eur).toFixed(0)}</span>
              </div>
              <div className="text-[10px] text-emerald-700 font-medium">Prezzo upgrade — possiedi versione precedente</div>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-zinc-900">€{Number(addon.effectivePrice ?? addon.price_eur).toFixed(0)}</div>
              <div className="text-[10px] text-zinc-500">una tantum · v{addon.version_year ?? '—'}</div>
            </>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => onPurchase(addon)}
          disabled={addon.owned || isPurchasing || isComingSoon}
          variant={addon.owned || isComingSoon ? 'outline' : 'default'}
          className={isBundle && !addon.owned && !isComingSoon ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          title={isComingSoon ? 'Disponibile al rilascio del contenuto' : undefined}
        >
          {addon.owned
            ? 'Nel tuo catalogo'
            : isComingSoon
              ? 'In arrivo'
              : isPurchasing
                ? 'Avvio…'
                : `Acquista v${addon.version_year ?? '?'}`}
        </Button>
      </CardFooter>
    </Card>
  );
}
